import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveyService } from '../services/survey.service';
import type {
  CreateTemplateRequest,
  UpdateTemplateRequest,
  CreateDispatchRequest,
  DispatchFilters,
  SubmitSurveyRequest,
} from '../types';

// Templates
export function useTemplates() {
  return useQuery({
    queryKey: ['survey-templates'],
    queryFn: () => surveyService.getTemplates(),
  });
}

export function useTemplate(id: string) {
  return useQuery({
    queryKey: ['survey-templates', id],
    queryFn: () => surveyService.getTemplateById(id),
    enabled: !!id,
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTemplateRequest) => surveyService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-templates'] });
    },
  });
}

export function useUpdateTemplate(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTemplateRequest) => surveyService.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-templates'] });
      queryClient.invalidateQueries({ queryKey: ['survey-templates', id] });
    },
  });
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => surveyService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-templates'] });
    },
  });
}

// Dispatches
export function useDispatches(params?: DispatchFilters) {
  return useQuery({
    queryKey: ['survey-dispatches', params],
    queryFn: () => surveyService.getDispatches(params),
  });
}

export function useDispatch(id: string) {
  return useQuery({
    queryKey: ['survey-dispatches', id],
    queryFn: () => surveyService.getDispatchById(id),
    enabled: !!id,
  });
}

export function useDispatchInvitations(dispatchId: string) {
  return useQuery({
    queryKey: ['survey-dispatches', dispatchId, 'invitations'],
    queryFn: () => surveyService.getDispatchInvitations(dispatchId),
    enabled: !!dispatchId,
  });
}

export function useDispatchResponses(dispatchId: string) {
  return useQuery({
    queryKey: ['survey-dispatches', dispatchId, 'responses'],
    queryFn: () => surveyService.getResponses(dispatchId),
    enabled: !!dispatchId,
  });
}

export function useCreateDispatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDispatchRequest) => surveyService.createDispatch(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-dispatches'] });
    },
  });
}

export function useCancelDispatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => surveyService.cancelDispatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-dispatches'] });
    },
  });
}

export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) => surveyService.resendInvitation(invitationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['survey-dispatches'] });
    },
  });
}

// Public survey
export function usePublicSurvey(token: string) {
  return useQuery({
    queryKey: ['public-survey', token],
    queryFn: () => surveyService.getPublicSurvey(token),
    enabled: !!token,
    retry: false,
  });
}

export function useSubmitSurvey(token: string) {
  return useMutation({
    mutationFn: (data: SubmitSurveyRequest) =>
      surveyService.submitSurvey(token, data),
  });
}
